const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_TABLE_FILTERS = Object.freeze({
  query: '',
  amountDirection: 'all',
  dateRange: 'all',
});

const SUPPORTED_AMOUNT_DIRECTIONS = new Set(['all', 'inflow', 'outflow', 'zero']);
const SUPPORTED_DATE_RANGES = new Set(['all', '30d', '90d', '365d']);
const AMOUNT_KEYS = ['amount', 'valueAccountCurrency', 'originalAmount', 'upac', 'upbc', 'uptc', 'value', 'quote'];
const GROUP_KEYS = ['assetId', 'accountId', 'providerAssetId'];

function coerceFiniteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeQuery(value) {
  return String(value || '').trim();
}

function resolveAmount(row) {
  for (let idx = 0; idx < AMOUNT_KEYS.length; idx += 1) {
    const key = AMOUNT_KEYS[idx];
    const value = coerceFiniteNumber(row?.[key]);
    if (value !== null) return value;
  }
  return null;
}

function resolveDate(row) {
  const raw = row?.date;
  if (raw === null || raw === undefined || raw === '') return null;

  const parsed = typeof raw === 'number' ? raw : Date.parse(raw);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

function resolveGroupValue(row) {
  for (let idx = 0; idx < GROUP_KEYS.length; idx += 1) {
    const key = GROUP_KEYS[idx];
    if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== '') return String(row[key]);
  }
  return null;
}

function matchesQuery(row, query) {
  if (!query) return true;
  const loweredQuery = query.toLowerCase();
  const haystack = Object.values(row || {})
    .filter((value) => typeof value === 'string')
    .join(' ')
    .toLowerCase();
  return haystack.includes(loweredQuery);
}

function matchesAmountDirection(row, amountDirection) {
  if (amountDirection === 'all') return true;
  const amount = resolveAmount(row);
  if (amount === null) return false;
  if (amountDirection === 'inflow') return amount > 0;
  if (amountDirection === 'outflow') return amount < 0;
  return amount === 0;
}

function matchesDateRange(row, dateRange, nowTimestamp) {
  if (dateRange === 'all') return true;
  const dateValue = resolveDate(row);
  if (dateValue === null) return false;

  let cutoff;
  switch (dateRange) {
    case '30d':
      cutoff = nowTimestamp - 30 * DAY_IN_MS;
      break;
    case '90d':
      cutoff = nowTimestamp - 90 * DAY_IN_MS;
      break;
    case '365d':
      cutoff = nowTimestamp - 365 * DAY_IN_MS;
      break;
    default:
      cutoff = null;
  }

  if (cutoff === null) return true;
  return dateValue >= cutoff;
}

export function normalizeTableFilters(filters = {}) {
  const query = normalizeQuery(filters.query);
  const amountDirection = SUPPORTED_AMOUNT_DIRECTIONS.has(filters.amountDirection) ? filters.amountDirection : DEFAULT_TABLE_FILTERS.amountDirection;
  const dateRange = SUPPORTED_DATE_RANGES.has(filters.dateRange) ? filters.dateRange : DEFAULT_TABLE_FILTERS.dateRange;

  return {
    query,
    amountDirection,
    dateRange,
  };
}

export function getActiveTableFilterCount(filters = {}) {
  const normalized = normalizeTableFilters(filters);
  let count = 0;
  if (normalized.query) count += 1;
  if (normalized.amountDirection !== DEFAULT_TABLE_FILTERS.amountDirection) count += 1;
  if (normalized.dateRange !== DEFAULT_TABLE_FILTERS.dateRange) count += 1;
  return count;
}

export function rowMatchesTableFilters(row, filters = {}, nowTimestamp = Date.now()) {
  const normalized = normalizeTableFilters(filters);
  return (
    matchesQuery(row, normalized.query)
    && matchesAmountDirection(row, normalized.amountDirection)
    && matchesDateRange(row, normalized.dateRange, nowTimestamp)
  );
}

export function applyTableFilters(rows = [], filters = {}, nowTimestamp = Date.now()) {
  const normalized = normalizeTableFilters(filters);
  if (getActiveTableFilterCount(normalized) === 0) return rows;

  const matchedGroups = new Set();
  const matchedDetailRows = new Set();

  rows.forEach((row, index) => {
    if (!row || row.isSummaryRow || row.isTotalRow) return;
    if (!rowMatchesTableFilters(row, normalized, nowTimestamp)) return;
    matchedDetailRows.add(index);
    const groupValue = resolveGroupValue(row);
    if (groupValue) matchedGroups.add(groupValue);
  });

  return rows.filter((row, index) => {
    if (!row) return false;
    if (row.isTotalRow) return false;
    if (row.isSummaryRow) {
      const summaryGroupValue = resolveGroupValue(row);
      return summaryGroupValue ? matchedGroups.has(summaryGroupValue) : false;
    }
    return matchedDetailRows.has(index);
  });
}
